import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { supabase } from '../supabaseClient';
import CoinSearch from '../CoinSearch';
import TechnicalTab from './watchlist/TechnicalTab';
import SmartMoneyTab from './watchlist/SmartMoneyTab';
import PriceActionTab from './watchlist/PriceActionTab';
import UAlgoTab from './watchlist/UAlgoTab';
import ElliottWaveTab from './watchlist/ElliottWaveTab';

const API_URL = process.env.REACT_APP_API_URL || 'https://tradesavvy-production.up.railway.app';

const DEFAULT_WATCHLIST = [
  { pair: 'BTC-USD', symbol: 'BTC', name: 'Bitcoin' },
  { pair: 'ETH-USD', symbol: 'ETH', name: 'Ethereum' },
  { pair: 'SOL-USD', symbol: 'SOL', name: 'Solana' },
  { pair: 'XRP-USD', symbol: 'XRP', name: 'Ripple' },
  { pair: 'SUI-USD', symbol: 'SUI', name: 'Sui' },
  { pair: 'LINK-USD', symbol: 'LINK', name: 'Chainlink' },
  { pair: 'ADA-USD', symbol: 'ADA', name: 'Cardano' },
  { pair: 'NEAR-USD', symbol: 'NEAR', name: 'NEAR Protocol' },
  { pair: 'DOGE-USD', symbol: 'DOGE', name: 'Dogecoin' },
];

interface WatchlistItem {
  pair: string;
  symbol: string;
  name: string;
}

interface WatchlistProps {
  userId: string;
}

const TABS = [
  { key: 'technical', label: 'Technical Analysis' },
  { key: 'smc', label: 'Smart Money' },
  { key: 'priceaction', label: 'Price Action' },
  { key: 'ualgo', label: 'UAlgo Trend' },
  { key: 'elliott', label: 'Elliott Wave' },
] as const;

type TabKey = typeof TABS[number]['key'];

const Watchlist: React.FC<WatchlistProps> = ({ userId }) => {
  const [watchlist, setWatchlist] = useState<WatchlistItem[]>([]);
  const [activeTab, setActiveTab] = useState<TabKey>('technical');

  useEffect(() => {
    loadWatchlist();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const loadWatchlist = async () => {
    try {
      const { data, error } = await supabase
        .from('watchlists')
        .select('coin_symbol, coin_name, position')
        .eq('user_id', userId)
        .order('position', { ascending: true });

      if (error) {
        console.error('Error loading watchlist:', error);
        setWatchlist(DEFAULT_WATCHLIST);
        return;
      }

      if (data && data.length > 0) {
        const items: WatchlistItem[] = data.map(row => ({
          pair: `${row.coin_symbol}-USD`,
          symbol: row.coin_symbol,
          name: row.coin_name,
        }));
        setWatchlist(items);
      } else {
        const inserts = DEFAULT_WATCHLIST.map((coin, index) => ({
          user_id: userId,
          coin_symbol: coin.symbol,
          coin_name: coin.name,
          position: index,
        }));

        const { error: insertError } = await supabase
          .from('watchlists')
          .insert(inserts);

        if (insertError) {
          console.error('Error inserting default watchlist:', insertError);
        }

        setWatchlist(DEFAULT_WATCHLIST);
      }
    } catch (error) {
      console.error('Error in loadWatchlist:', error);
      setWatchlist(DEFAULT_WATCHLIST);
    }
  };

  const handleAddCoin = async (pair: string, symbol: string, name: string) => {
    const newItem: WatchlistItem = { pair, symbol, name };
    const updated = [...watchlist, newItem];
    setWatchlist(updated);

    try {
      const { error } = await supabase
        .from('watchlists')
        .insert({
          user_id: userId,
          coin_symbol: symbol,
          coin_name: name,
          position: watchlist.length,
        });

      if (error) {
        console.error('Error saving to watchlist:', error);
        setWatchlist(watchlist);
      }
    } catch (error) {
      console.error('Error adding coin:', error);
      setWatchlist(watchlist);
    }
  };

  const handleRemoveCoin = async (symbol: string) => {
    if (watchlist.length <= 1) {
      alert('You need at least one coin on your dashboard.');
      return;
    }

    const updated = watchlist.filter(w => w.symbol !== symbol);
    setWatchlist(updated);

    try {
      const { error } = await supabase
        .from('watchlists')
        .delete()
        .eq('user_id', userId)
        .eq('coin_symbol', symbol);

      if (error) {
        console.error('Error removing from watchlist:', error);
        setWatchlist(watchlist);
      }
    } catch (error) {
      console.error('Error removing coin:', error);
      setWatchlist(watchlist);
    }
  };

  const renderTab = () => {
    switch (activeTab) {
      case 'technical':
        return <TechnicalTab watchlist={watchlist} apiUrl={API_URL} />;
      case 'smc':
        return <SmartMoneyTab watchlist={watchlist} apiUrl={API_URL} />;
      case 'priceaction':
        return <PriceActionTab watchlist={watchlist} apiUrl={API_URL} />;
      case 'ualgo':
        return <UAlgoTab watchlist={watchlist} apiUrl={API_URL} />;
      case 'elliott':
        return <ElliottWaveTab watchlist={watchlist} apiUrl={API_URL} />;
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div className="page-title">Watchlist</div>
      </div>

      {/* Search + Coin pills */}
      <div style={{ marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center' }}>
        <div style={{ flex: 1 }}>
          <CoinSearch
            currentCoins={watchlist.map(w => w.pair)}
            onAddCoin={handleAddCoin}
            maxCoins={10}
            apiUrl={API_URL}
          />
        </div>
      </div>

      {/* Coin pills */}
      {watchlist.length > 0 && (
        <div style={{ marginBottom: 16, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {watchlist.map(coin => (
            <div
              key={coin.symbol}
              className="flex items-center gap-1 px-2.5 py-1 bg-gray-800 border border-gray-700 rounded-full text-xs text-gray-300 group"
            >
              <span className="font-mono">{coin.symbol}</span>
              <button
                onClick={() => handleRemoveCoin(coin.symbol)}
                className="ml-0.5 p-0.5 rounded-full hover:bg-red-500/20 hover:text-red-400 text-gray-600 opacity-0 group-hover:opacity-100 transition-all"
                title={`Remove ${coin.symbol}`}
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="tabs" style={{ marginBottom: 20 }}>
        {TABS.map(tab => (
          <button
            key={tab.key}
            className={`tab ${activeTab === tab.key ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Active tab content */}
      {renderTab()}
    </div>
  );
};

export default Watchlist;
